import { AnalyticsLevel } from 'packages/cost-management/group-levels';
import { useCallback } from 'react';
import { generatePath, useLocation, useRouteMatch } from 'react-router-dom';

// 2026-08-04 Used Cursor (Opus 5) for initial design
// 2026-09-25 Used Cursor (Opus 5.5) to make code more human-readable

export const ANALYTICS_BASE_PATH = '/analytics';
const REQUEST_PATH = `${ANALYTICS_BASE_PATH}/requests/:requestId`;

/**
 * Every analytics page's route pattern. Sample groups, task groups, tasks and
 * contributors also belong to a request, so they can be reached under one as well
 * (`requestPath`), which keeps the user in the Requests tree while drilling down.
 */
export const ANALYTICS_ROUTES: Record<
  AnalyticsLevel,
  { path: string; requestPath?: string }
> = {
  AllPipelines: { path: `${ANALYTICS_BASE_PATH}/pipelines` },
  Pipeline: { path: `${ANALYTICS_BASE_PATH}/pipeline/:token` },
  Workflow: { path: `${ANALYTICS_BASE_PATH}/workflow/:token` },
  SampleGroup: {
    path: `${ANALYTICS_BASE_PATH}/samplegroup/:token`,
    requestPath: `${REQUEST_PATH}/samplegroup/:token`,
  },
  TaskGroup: {
    path: `${ANALYTICS_BASE_PATH}/taskgroup/:token`,
    requestPath: `${REQUEST_PATH}/taskgroup/:token`,
  },
  Task: {
    path: `${ANALYTICS_BASE_PATH}/task/:token`,
    requestPath: `${REQUEST_PATH}/task/:token`,
  },
  Contributor: {
    path: `${ANALYTICS_BASE_PATH}/contributor/:token`,
    requestPath: `${REQUEST_PATH}/contributor/:token`,
  },
  AllRequests: { path: `${ANALYTICS_BASE_PATH}/requests` },
  RequestId: { path: `${ANALYTICS_BASE_PATH}/requests/:token` },
};

/** Both route patterns of a level, for a `<Route path>`. */
export const analyticsRoutePaths = (level: AnalyticsLevel): string[] => {
  const { path, requestPath } = ANALYTICS_ROUTES[level];
  return requestPath ? [path, requestPath] : [path];
};

/**
 * URL of an analytics page. Given a `requestId`, levels that belong to a request are
 * nested under it; every other level ignores it, which is what takes a link back out of
 * the request.
 */
export const analyticsPath = (
  level: AnalyticsLevel,
  token = '',
  requestId?: string
): string => {
  const { path, requestPath } = ANALYTICS_ROUTES[level];
  return requestId && requestPath
    ? generatePath(requestPath, { requestId, token })
    : generatePath(path, { token });
};

/**
 * The request being viewed, or the one the current page was drilled into from. A sample
 * group belongs to both a workflow and a request, so this is what tells its breadcrumbs
 * which way the user came in.
 */
export const useRequestId = (): string | undefined =>
  useRouteMatch<{ requestId: string }>(REQUEST_PATH)?.params.requestId;

interface AnalyticsLocation {
  pathname: string;
  search: string;
}

/**
 * Builds links between analytics pages that keep the current filters, so drilling into a
 * row or stepping back up a breadcrumb does not reset the date range or the menus.
 */
export const useAnalyticsLink = (): ((
  level: AnalyticsLevel,
  token?: string
) => AnalyticsLocation) => {
  const { search } = useLocation();
  const requestId = useRequestId();

  return useCallback(
    (level, token) => ({
      pathname: analyticsPath(level, token, requestId),
      search,
    }),
    [search, requestId]
  );
};
